import { Bell } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { Notification } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	useMarkAllNotificationsRead,
	useMarkNotificationRead,
	useNotifications,
} from "@/features/notifications/hooks/use-notifications.ts";
import {
	formatNotificationTime,
	notificationMessage,
} from "@/features/notifications/utils/format.ts";
import {
	notificationItemMeta,
	notificationPath,
} from "@/features/notifications/utils/item.ts";
import { useObras } from "@/features/stages/hooks/use-obras.ts";

export function NotificationButton() {
	const [open, setOpen] = useState(false);
	const navigate = useNavigate();
	const { obra: obraKey } = useParams();
	const { data: obras } = useObras();
	const { data } = useNotifications();
	const markRead = useMarkNotificationRead();
	const markAllRead = useMarkAllNotificationsRead();

	const notifications = data?.items ?? [];
	const unreadCount = data?.unread_count ?? 0;

	const activate = (notification: Notification) => {
		if (!notification.read_at) {
			markRead.mutate(notification.id);
		}
		const path = notificationPath(notification, { obraKey, obras });
		if (path) {
			setOpen(false);
			navigate(path);
		}
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="icon"
					className="notification-button"
					aria-label={`Notificações: ${unreadCount} não lidas`}
				>
					<Bell />
					{unreadCount > 0 && (
						<span className="notification-button__badge">{unreadCount}</span>
					)}
				</Button>
			</PopoverTrigger>

			<PopoverContent align="end" className="notification-panel">
				<div className="notification-panel__header">
					<p className="notification-panel__title">Notificações</p>
					{unreadCount > 0 && (
						<button
							type="button"
							className="notification-panel__mark-all"
							onClick={() => markAllRead.mutate()}
						>
							Marcar todas como lidas
						</button>
					)}
				</div>

				<div className="notification-panel__list">
					{notifications.length === 0 && (
						<p className="notification-panel__empty">
							Nenhuma notificação por aqui.
						</p>
					)}
					{notifications.map((notification) => {
						const { Icon, kind } = notificationItemMeta(notification.type);
						return (
							<button
								key={notification.id}
								type="button"
								className="notification-item"
								onClick={() => activate(notification)}
							>
								<div className="notification-item__icon" data-kind={kind}>
									<Icon />
								</div>
								<div className="notification-item__body">
									<p className="notification-item__title">
										{notificationMessage(notification)}
									</p>
								</div>
								<span className="notification-item__time">
									{formatNotificationTime(notification.created_at)}
								</span>
								{!notification.read_at && (
									<span className="notification-item__unread-dot" />
								)}
							</button>
						);
					})}
				</div>
			</PopoverContent>
		</Popover>
	);
}
